import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { App } from '../App.tsx';
import { normalizeUsername } from '../notebook.ts';
import { progressClient } from '../progress-client.ts';
import { ProgressSession } from '../progress-session.ts';

export function Application() {
  const [mode, setMode] = useState<'loading' | 'local' | 'shared' | 'error'>('loading');
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const response = await fetch('/api/config', {
          cache: 'no-store',
          signal: AbortSignal.timeout(10000),
        });
        const vite =
          ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname) &&
          response.headers.get('content-type')?.includes('text/html');
        if (vite) {
          if (live) setMode('local');
          return;
        }
        if (!response.ok) throw new Error('Configuration unavailable');
        const config = await response.json();
        if (typeof config.enabled !== 'boolean') throw new Error('Invalid configuration');
        if (live) setMode(config.enabled ? 'shared' : 'local');
      } catch {
        if (live) setMode('error');
      }
    })();
    return () => {
      live = false;
    };
  }, []);
  if (mode === 'local') return <App />;
  if (mode === 'shared') return <SharedProgress />;
  return (
    <App
      blocked
      account={
        <div className="account-control">
          {mode === 'loading' ? (
            <span role="status">Opening…</span>
          ) : (
            <button className="account-link" onClick={() => location.reload()}>
              Retry connection
            </button>
          )}
        </div>
      }
      notice={
        mode === 'error' ? (
          <p className="storage-warning" role="alert">
            Could not connect. Your progress has not been changed.
          </p>
        ) : undefined
      }
    />
  );
}
function rememberedName() {
  try {
    return normalizeUsername(localStorage.getItem('mentat.username') ?? '') ?? '';
  } catch {
    return '';
  }
}
function rememberName(name: string) {
  try {
    if (name) localStorage.setItem('mentat.username', name);
    else localStorage.removeItem('mentat.username');
  } catch {
    /* Practice still works when remembering a name is unavailable. */
  }
}
function SharedProgress() {
  const [initialName] = useState(rememberedName);
  const [name, setName] = useState(initialName);
  const [active, setActive] = useState<{ name: string; session: ProgressSession } | null>(null);
  const [restoring, setRestoring] = useState(!!initialName);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(!!initialName);
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const request = useRef(0);
  async function openName(value: string) {
    const username = normalizeUsername(value);
    if (!username) {
      setError('Use 2–32 letters, numbers, hyphens or underscores. Start with a letter or number.');
      return;
    }
    const id = ++request.current;
    setBusy(true);
    setError('');
    try {
      const client = progressClient(username);
      const saved = await client.get();
      if (id !== request.current) return;
      rememberName(username);
      setName(username);
      setRestoring(false);
      setOpen(false);
      setActive({ name: username, session: new ProgressSession(client, saved) });
    } catch (error) {
      if (id === request.current) {
        setError(error instanceof Error ? error.message : 'Could not open progress. Try again.');
        setOpen(true);
      }
    } finally {
      if (id === request.current) setBusy(false);
    }
  }
  useEffect(() => {
    if (initialName) void openName(initialName);
    return () => {
      request.current++;
    };
  }, [initialName]);
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const outside = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  function logout() {
    request.current++;
    rememberName('');
    setName('');
    setRestoring(false);
    setBusy(false);
    setError('');
    setOpen(false);
    setActive(null);
  }
  if (active)
    return (
      <NotebookView
        key={active.name}
        username={active.name}
        session={active.session}
        onLogout={logout}
      />
    );
  return (
    <App
      blocked={busy || restoring}
      account={
        <div className="account-control" ref={container}>
          <button
            ref={trigger}
            className="account-link"
            aria-expanded={open}
            aria-controls="username-panel"
            onClick={() => setOpen(!open)}
          >
            {restoring
              ? busy
                ? 'Opening your progress…'
                : 'Open saved progress'
              : 'Use a username'}
          </button>
          {open && (
            <section className="username-panel" id="username-panel" aria-label="Use a username">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void openName(name);
                }}
              >
                <div className="username-label">
                  <label htmlFor="username">Username</label>
                  <span>No password</span>
                </div>
                <div className="username-entry">
                  <input
                    ref={input}
                    id="username"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    maxLength={32}
                    value={name}
                    disabled={busy}
                    onChange={(event) => setName(event.target.value)}
                    required
                    aria-describedby="username-help"
                  />
                  <button disabled={busy}>{busy ? 'Opening…' : 'Continue'}</button>
                </div>
                <p id="username-help">Same name, same progress on any device. Names are shared.</p>
                {error && (
                  <p className="account-error" role="alert">
                    {error}
                  </p>
                )}
                {restoring && !busy && (
                  <button type="button" className="account-link" onClick={logout}>
                    Continue without a username
                  </button>
                )}
              </form>
            </section>
          )}
        </div>
      }
    />
  );
}
function NotebookView({
  username,
  session,
  onLogout,
}: {
  username: string;
  session: ProgressSession;
  onLogout: () => void;
}) {
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const focus = () => {
      void session.refresh();
    };
    const hide = () => {
      if (document.visibilityState === 'hidden') void session.flush();
    };
    const unload = (event: BeforeUnloadEvent) => {
      if (session.hasPending()) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('beforeunload', unload);
    return () => {
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', hide);
      window.removeEventListener('beforeunload', unload);
    };
  }, [session]);
  return (
    <App
      blocked={busy}
      account={
        <div className="account-control account-active">
          <div className="account-identity">
            <span className="account-name" title={username}>
              {username}
            </span>
            <span className="account-status" role="status">
              {snapshot.status === 'saving'
                ? 'Saving…'
                : snapshot.status === 'error'
                  ? 'Not saved'
                  : ''}
            </span>
          </div>
          <button
            className="account-link"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              if (await session.flush()) onLogout();
              else setBusy(false);
            }}
          >
            {busy ? 'Saving…' : 'Log out'}
          </button>
        </div>
      }
      notice={
        snapshot.message ? (
          <p className="storage-warning" role="status">
            {snapshot.message}{' '}
            {snapshot.status === 'error' && (
              <button
                onClick={() => {
                  void session.flush();
                }}
              >
                Retry save
              </button>
            )}
            {snapshot.status !== 'error' && snapshot.message.startsWith('Could not check') && (
              <button
                onClick={() => {
                  void session.refresh();
                }}
              >
                Retry
              </button>
            )}
          </p>
        ) : undefined
      }
      shared={{
        document: snapshot.document,
        generation: snapshot.generation,
        edit: (update, remount) => session.edit(update, remount),
      }}
    />
  );
}
